import { Dimensions, Platform, StyleSheet } from 'react-native';
import { appTheme } from '../../shared/theme';
import type { ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { androidResolveShadow } from '../../shared/theme/androidUiParity';
import { centeredLabelText, pillButtonLabelCentered } from '../../shared/theme/sharedStyles';
import { androidFlatSurfaceStyle, androidScaleFont, androidScaleSize } from '../../shared/theme/sharedStyles';
import {
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  studioSectionLabelStyle,
} from '../../shared/theme/studio/stackScreenShell';
import { resolveWebLayoutWidth } from '../../shared/platform/webLayout';

const WINDOW_WIDTH = resolveWebLayoutWidth(Dimensions.get('window').width);
export const SLIDE_WIDTH = Math.min(WINDOW_WIDTH - STACK_SCROLL_GUTTER * 2, 360);
/** Hero image locked to 2:1 (width:height). Width drives height — do not androidScaleSize here (breaks ratio vs full-width card). */
export const SLIDE_IMAGE_HEIGHT = Math.round(SLIDE_WIDTH / 2);
/** Content: image = 80%, body = 20%. */
const SLIDE_CONTENT_HEIGHT = Math.round(SLIDE_IMAGE_HEIGHT / 0.8);
export const SLIDE_BODY_EXTRA = SLIDE_CONTENT_HEIGHT - SLIDE_IMAGE_HEIGHT;
/** Card frame height (image + body). */
export const SLIDE_CARD_INNER_HEIGHT = SLIDE_CONTENT_HEIGHT;
/** Outer carousel slot inset so the drop shadow is never clipped. */
const SLIDE_BORDER_INSET = appTheme.spacing.xs;
export const SLIDE_CARD_HEIGHT = SLIDE_CARD_INNER_HEIGHT + SLIDE_BORDER_INSET * 2;
/**
 * Custom-plan category carousel only — ~half the standard slide height so the builder form gets more room.
 * Body keeps a readable minimum; image takes the remaining compact height.
 */
export const CUSTOM_SLIDE_IMAGE_HEIGHT = Math.round(SLIDE_IMAGE_HEIGHT / 2.5);
export const CUSTOM_SLIDE_BODY_EXTRA = 0;
export const CUSTOM_SLIDE_CARD_INNER_HEIGHT = CUSTOM_SLIDE_IMAGE_HEIGHT + CUSTOM_SLIDE_BODY_EXTRA;
export const CUSTOM_SLIDE_CARD_HEIGHT = CUSTOM_SLIDE_CARD_INNER_HEIGHT + SLIDE_BORDER_INSET * 2;
const SLIDE_BODY_PAD_TOP = 4;
const SLIDE_BODY_PAD_BOTTOM = 6;
export const SLIDE_GAP = appTheme.spacing.md;
/** Legacy stack pitch — horizontal carousel no longer uses vertical offset. */
export const SLIDE_STACK_OFFSET = 0;
/** Stacked carousel paginator row + gap below slide (matches v3 StackedCarousel layout). */
export const SUBSCRIPTION_CAROUSEL_CHROME_EXTRA = 28;
/** Flow1 category list: image column width & row min height (spacing tokens). */
/** Tag flow: `PlanSection` / `restructuredData` (tag strip + category row cards) — left image column width. */
export const TAG_FLOW_CATEGORY_IMAGE_EXTENT = androidScaleSize(appTheme.spacing.xxl * 4 + appTheme.spacing.md);
/** Plan catalog card — shared inner chrome radius (overlays, meta chips, select). */
export const PLAN_CARD_CHIP_RADIUS = 12;
/** Hero image height — compact row, matches tag-flow category thumb. */
export const PLAN_CARD_IMAGE_HEIGHT = TAG_FLOW_CATEGORY_IMAGE_EXTENT;

export function createSubscriptionListStyles(c: ThemeColors, window_width = Dimensions.get('window').width) {
  const count_tile_extent = Math.floor((window_width - appTheme.spacing.md * 2 - 8 * 3) / 4);
  const sf = androidScaleFont;
  const ss = androidScaleSize;
  const option_row_bg = (hexToRgba(c.text, 0.04) as string) || c.background;
  const option_row_active_bg = (hexToRgba(c.primary, 0.1) as string) || c.primaryUltra;
  const body_secondary = hexToRgba(c.text, 0.72);
  const muted_label = hexToRgba(c.text, 0.58);
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    listContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
    },
    gridItemCell: {
      width: '48%',
      marginBottom: STACK_SECTION_GAP,
    },
    gridWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      gap: STACK_SECTION_GAP,
    },
    /** Scroll body fills the screen; list content gets `paddingTop` = measured pinned filter height so items scroll under the frosted bar. */
    listScrollUnderPin: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
    },
    /** Wraps Blur + tabs/slider: fixed to top, above the scrolling list. */
    pinnedFilterChrome: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      zIndex: 8,
      overflow: 'hidden',
    },
    listHeader: {
      height: appTheme.spacing.sm,
    },
    topHeaderBlock: {
      paddingTop: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.sm,
    },
    floatingBackWrap: {
      position: 'absolute',
      zIndex: 30,
      left: appTheme.spacing.sm,
    },
    floatingBackButton: {
      width: ss(40),
      height: ss(40),
      borderRadius: ss(14),
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        ios: {
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.09,
          shadowRadius: 10,
        },
        android: androidFlatSurfaceStyle(),
      }),
    },
    filterBar: {
      minHeight: appTheme.sizes.breadcrumbHeight,
      justifyContent: 'center',
    },
    filterBarContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingVertical: appTheme.spacing.sm,
      gap: appTheme.spacing.sm,
      flexDirection: 'row',
      alignItems: 'center',
    },
    filterChip: {
      borderRadius: appTheme.borderRadius.pill,
      backgroundColor: c.surface,
      overflow: 'hidden',
      ...androidFlatSurfaceStyle(),
    },
    filterChipInner: {
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.sm - appTheme.spacing.us,
      alignItems: 'center',
      justifyContent: 'center',
    },
    filterChipActive: {
      backgroundColor: c.primary,
    },
    filterChipText: {
      ...appTheme.typography.label,
      color: c.textSecondary,
    },
    filterChipTextActive: {
      color: c.onPrimary,
    },
    categorySlider: {
      alignSelf: 'stretch',
      width: '100%',
    },
    carouselTouchLayer: {
      alignSelf: 'stretch',
    },
    categorySliderContent: {
      paddingVertical: appTheme.spacing.sm,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: SLIDE_GAP,
    },
    /**
     * Clickable with card shadow uses a two-layer host; without explicit min height the flex-fill
     * inner surface can collapse. Match hero image block height.
     */
    categorySlideVisualRoot: {
      width: '100%',
      height: SLIDE_CARD_INNER_HEIGHT,
      alignSelf: 'stretch',
    },
    categorySlideFrame: {
      width: '100%',
      height: SLIDE_CARD_INNER_HEIGHT,
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: c.surface,
      overflow: 'hidden',
    },
    categorySlideFrameActive: {},
    categorySlide: {
      width: '100%',
      height: '100%',
      flexDirection: 'column',
      backgroundColor: c.surface,
      overflow: 'hidden',
    },
    categorySlideImageWrap: {
      width: '100%',
      height: SLIDE_IMAGE_HEIGHT,
      flexShrink: 0,
      flexGrow: 0,
      position: 'relative',
      backgroundColor: c.skeleton,
      overflow: 'hidden',
    },
    categorySlideImage: {
      width: '100%',
      height: '100%',
    },
    categorySlidePlaceholder: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.skeleton,
    },
    categorySlideBody: {
      height: SLIDE_BODY_EXTRA,
      flexShrink: 0,
      flexGrow: 0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: SLIDE_BODY_PAD_TOP,
      paddingBottom: SLIDE_BODY_PAD_BOTTOM,
      backgroundColor: c.surface,
      overflow: 'hidden',
    },
    categorySlideTitle: {
      flex: 1,
      minWidth: 0,
      ...appTheme.typography.bodyMedium,
      fontSize: sf(15),
      fontWeight: '800',
      letterSpacing: -0.2,
      color: c.text,
      lineHeight: sf(19),
    },
    categorySlideSeeMore: {
      flexShrink: 0,
    },
    categorySlideFooterRow: {
      flexShrink: 0,
    },
    categorySlideSeeMoreInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.xs,
      borderRadius: appTheme.borderRadius.pill,
      backgroundColor: (hexToRgba(c.primary, 0.1) as string) || c.primaryUltra,
    },
    categorySlideSeeMoreText: {
      ...appTheme.typography.caption,
      fontSize: sf(12),
      lineHeight: sf(14),
      color: c.primary,
      fontWeight: '800',
    },
    stackedCarouselShell: {
      flex: 1,
      margin: SLIDE_BORDER_INSET,
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      overflow: 'visible',
      borderWidth: 0,
      shadowColor: c.shadowColor,
    },
    stackedCarouselContainer: {
      alignSelf: 'stretch',
      width: '100%',
    },
    subscriptionCarouselPaginator: {
      minHeight: 24,
      justifyContent: 'center',
      alignItems: 'center',
    },
    carouselHost: {
      position: 'relative',
      width: '100%',
      alignSelf: 'stretch',
    },
    categorySlideOverlayFooter: {
      position: 'absolute' as const,
      left: appTheme.spacing.md + SLIDE_BORDER_INSET,
      right: appTheme.spacing.md + SLIDE_BORDER_INSET,
      bottom: SLIDE_BORDER_INSET,
      height: SLIDE_BODY_EXTRA,
      alignItems: 'flex-end',
      justifyContent: 'center',
      zIndex: 4,
    },
    categorySlideOverlaySeeMore: {
      alignSelf: 'flex-end',
    },
    categorySlideSeeMoreHitTarget: {
      marginTop: 0,
    },
    categorySlideSeeMoreHitArea: {
      minWidth: androidScaleSize(104),
      minHeight: androidScaleSize(36),
    },
    tagFlowCategoryCard: {
      marginBottom: STACK_SECTION_GAP,
      minHeight: TAG_FLOW_CATEGORY_IMAGE_EXTENT,
      overflow: 'visible',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      shadowColor: c.shadowColor,
    },
    tagFlowCategoryCardInner: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
      minHeight: TAG_FLOW_CATEGORY_IMAGE_EXTENT,
      overflow: 'hidden',
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: c.surface,
    },
    tagFlowCategoryCardImageWrap: {
      width: TAG_FLOW_CATEGORY_IMAGE_EXTENT,
      height: TAG_FLOW_CATEGORY_IMAGE_EXTENT,
      flexShrink: 0,
      overflow: 'hidden',
      backgroundColor: c.borderLight,
      borderTopStartRadius: DASH_STUDIO_RADIUS,
      borderBottomStartRadius: DASH_STUDIO_RADIUS,
    },
    tagFlowCategoryCardImage: {
      width: '100%',
      height: '100%',
    },
    tagFlowCategoryCardPlaceholder: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.borderLight,
    },
    tagFlowCategoryCardBody: {
      flex: 1,
      justifyContent: 'center',
      minWidth: 0,
      paddingVertical: appTheme.spacing.md,
      paddingStart: appTheme.spacing.md,
      paddingEnd: appTheme.spacing.sm,
      gap: appTheme.spacing.xs,
    },
    tagFlowCategoryCardTitle: {
      ...appTheme.typography.bodyMedium,
      fontSize: sf(15),
      fontWeight: '800',
      color: c.text,
      letterSpacing: -0.15,
    },
    tagFlowCategoryCardDescription: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(13),
      color: body_secondary,
      lineHeight: sf(18),
    },
    tagFlowCategoryCardMeta: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: appTheme.spacing.xs,
    },
    tagFlowCategoryCardMetaBox: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: hexToRgba(c.primary, 0.12),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.primary, 0.22),
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.xs,
      borderRadius: appTheme.borderRadius.pill,
      gap: appTheme.spacing.xs,
    },
    tagFlowCategoryCardMetaQty: {
      ...appTheme.typography.label,
      fontSize: appTheme.typography.bodySmall.fontSize,
      color: c.primary,
      fontWeight: '700',
    },
    tagFlowCategoryCardMetaLabel: {
      ...appTheme.typography.caption,
      fontSize: appTheme.typography.captionMini.fontSize,
      color: c.primary,
      fontWeight: '600',
    },
    tagFlowCategoryCardChevron: {
      alignSelf: 'center',
      marginEnd: appTheme.spacing.sm,
      flexShrink: 0,
    },
    sectionHeader: {
      marginBottom: appTheme.spacing.sm,
      marginTop: STACK_SECTION_GAP,
    },
    sectionHeaderEyebrow: {
      ...studioSectionLabelStyle(c),
      marginBottom: appTheme.spacing.xs,
    },
    sectionHeaderPressable: {
      width: '100%',
    },
    sectionHeaderPressableInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: appTheme.spacing.xs,
      width: '100%',
    },
    sectionHeaderTitle: {
      ...appTheme.typography.subtitle,
      fontSize: sf(18),
      fontWeight: '800',
      color: c.text,
      flex: 1,
    },
    cardWrap: {
      marginBottom: STACK_SECTION_GAP,
    },
    planCard: {
      overflow: 'visible',
      ...studioCardShell(c),
      /** Always reserve selected border width so radius math stays stable (no layout jump). */
      borderWidth: 2,
      borderColor: 'transparent',
      borderRadius: DASH_STUDIO_RADIUS,
      shadowColor: c.shadowColor,
      ...androidResolveShadow(appTheme.shadows.sm),
    },
    planCardSelected: {
      borderColor: c.primary,
    },
    planCardInnerSelected: {
      borderWidth: 0,
    },
    planCardSelectedBadge: {
      position: 'absolute',
      top: appTheme.spacing.sm,
      /** Opposite side from price badges (`end`) so the check never covers the price. */
      start: appTheme.spacing.sm,
      zIndex: 4,
      width: ss(26),
      height: ss(26),
      borderRadius: ss(13),
      backgroundColor: c.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    mergedDayPillBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.xs,
      paddingBottom: appTheme.spacing.sm,
    },
    mergedDayPillScrollContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
      paddingVertical: appTheme.spacing.us,
    },
    mergedDayPill: {
      borderRadius: appTheme.borderRadius.pill,
      backgroundColor: c.surface,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderSubtle,
      overflow: 'hidden',
      ...androidFlatSurfaceStyle(),
    },
    mergedDayPillActive: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    mergedDayPillInner: {
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.xs + 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: ss(32),
    },
    mergedDayPillText: {
      ...appTheme.typography.label,
      fontSize: sf(12),
      fontWeight: '700',
      color: c.textSecondary,
    },
    mergedDayPillTextActive: {
      color: c.onPrimary,
    },
    mergedCheckoutFooter: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 20,
      borderTopLeftRadius: DASH_STUDIO_RADIUS + 4,
      borderTopRightRadius: DASH_STUDIO_RADIUS + 4,
      overflow: 'hidden',
      backgroundColor: c.primary,
      ...Platform.select({
        ios: {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: -3 },
          shadowOpacity: 0.18,
          shadowRadius: 10,
        },
        android: { elevation: 10 },
        default: {},
      }),
    },
    mergedCheckoutFooterPressable: {
      width: '100%',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      minHeight: 72,
      justifyContent: 'center',
    },
    mergedCheckoutFooterPressablePressed: {
      opacity: 0.92,
    },
    mergedCheckoutFooterRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.md,
    },
    mergedCheckoutFooterTextBlock: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'center',
    },
    mergedCheckoutFooterPrice: {
      ...(appTheme.typography?.body ?? {}),
      fontSize: sf(18),
      lineHeight: sf(22),
      fontWeight: '800',
      color: c.onPrimary ?? c.surface,
      marginBottom: 4,
    },
    mergedCheckoutFooterHint: {
      ...(appTheme.typography?.bodySmall ?? {}),
      fontSize: sf(13),
      lineHeight: sf(18),
      fontWeight: '600',
      color: hexToRgba(c.onPrimary, 0.88),
    },
    mergedCheckoutFooterCta: {
      width: ss(40),
      height: ss(40),
      borderRadius: ss(20),
      backgroundColor: hexToRgba(c.onPrimary, 0.18),
      alignItems: 'center',
      justifyContent: 'center',
    },
    planCardHero: {
      width: '100%',
      height: PLAN_CARD_IMAGE_HEIGHT,
      backgroundColor: c.borderLight,
      position: 'relative',
    },
    planCardBody: {
      padding: appTheme.spacing.lg,
      gap: appTheme.spacing.sm,
    },
    planCardMetaChipsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: appTheme.spacing.sm,
    },
    planCardMetaChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.xs,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      backgroundColor: (hexToRgba(c.text, 0.05) as string) || c.background,
    },
    planCardMetaChipText: {
      ...appTheme.typography.caption,
      fontSize: sf(12),
      fontWeight: '700',
      color: c.textSecondary,
    },
    planCardDurationChip: {
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.xs,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      backgroundColor: (hexToRgba(c.primary, 0.1) as string) || c.primaryUltra,
    },
    planCardDurationText: {
      ...appTheme.typography.caption,
      fontSize: sf(12),
      fontWeight: '800',
      color: c.primary,
    },
    planCardCtaRow: {
      marginTop: appTheme.spacing.xs,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.xs,
      paddingVertical: appTheme.spacing.sm + appTheme.spacing.us,
      paddingHorizontal: appTheme.spacing.md,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      backgroundColor: c.primary,
    },
    planCardCtaText: {
      ...appTheme.typography.bodyMedium,
      fontSize: sf(14),
      fontWeight: '800',
      color: c.onPrimary,
    },
    planCardDetailsLink: {
      alignSelf: 'center',
      paddingVertical: appTheme.spacing.xs,
    },
    planCardDetailsText: {
      ...appTheme.typography.caption,
      fontSize: sf(12),
      fontWeight: '700',
      color: c.primary,
      textDecorationLine: 'underline',
    },
    planCardInner: {
      width: '100%',
      overflow: 'hidden',
      /** Inset by the reserved 2px card border so selected stroke and fill radii match (no corner gap). */
      borderRadius: DASH_STUDIO_RADIUS - 2,
      backgroundColor: c.surface,
    },
    planCardImageBlock: {
      width: '100%',
      height: PLAN_CARD_IMAGE_HEIGHT,
      backgroundColor: c.borderLight,
      position: 'relative',
      overflow: 'hidden',
    },
    planCardImage: {
      width: '100%',
      height: '100%',
      resizeMode: 'cover',
    },
    planCardImagePlaceholder: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.borderLight,
    },
    planCardImageBadges: {
      position: 'absolute',
      top: appTheme.spacing.sm,
      end: appTheme.spacing.sm,
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
      zIndex: 2,
    },
    /** Shared overlay chip chrome — offer + price must match exactly. */
    badgeOffer: {
      backgroundColor: c.error,
      paddingHorizontal: appTheme.spacing.sm + 2,
      paddingVertical: 5,
      minHeight: 28,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.white, 0.18),
      ...androidResolveShadow({
        shadowColor: c.error,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.28,
        shadowRadius: 3,
        elevation: 2,
      }),
    },
    badgeOfferText: {
      fontSize: sf(11),
      lineHeight: sf(13),
      fontWeight: '800',
      letterSpacing: 0.55,
      color: c.white,
      textTransform: 'uppercase',
    },
    badgePrice: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: hexToRgba(c.black, 0.68),
      paddingHorizontal: appTheme.spacing.sm + 2,
      paddingVertical: 5,
      minHeight: 28,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.white, 0.2),
      justifyContent: 'center',
    },
    badgePriceStrike: {
      fontSize: sf(11),
      lineHeight: sf(14),
      fontWeight: '700',
      color: hexToRgba(c.white, 0.7),
      textDecorationLine: 'line-through',
    },
    badgePriceText: {
      fontSize: sf(13),
      lineHeight: sf(16),
      fontWeight: '800',
      letterSpacing: -0.15,
      color: c.white,
    },
    planCardFooter: {
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.sm + 2,
      paddingBottom: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
    },
    planCardCompactRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
    },
    planCardCompactCopy: {
      flex: 1,
      minWidth: 0,
      gap: 4,
      justifyContent: 'center',
    },
    planCardTitle: {
      ...appTheme.typography.bodyMedium,
      fontSize: sf(15),
      lineHeight: sf(19),
      fontWeight: '800',
      letterSpacing: -0.2,
      color: c.text,
    },
    planCardColumnsRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: appTheme.spacing.sm,
    },
    planCardNutritionBlock: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
    },
    planCardPlainMetaBlock: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
      justifyContent: 'center',
    },
    planCardPlainMeta: {
      fontSize: sf(12),
      lineHeight: sf(16),
      fontWeight: '600',
    },
    planCardScaledLine: {
      fontSize: sf(13),
      lineHeight: sf(17),
      fontWeight: '700',
    },
    planCardCalorieLine: {
      fontSize: sf(12),
      lineHeight: sf(16),
      fontWeight: '600',
    },
    planCardTagsStack: {
      flexGrow: 0,
      flexShrink: 0,
      minWidth: 118,
      gap: 4,
      justifyContent: 'center',
    },
    planCardTagsCluster: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    planCardMetaTag: {
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      borderWidth: StyleSheet.hairlineWidth * 2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    planCardMetaTagFlex: {
      flexGrow: 0,
      flexShrink: 0,
    },
    planCardDaysTagFull: {
      alignSelf: 'stretch',
    },
    planCardMetaTagText: {
      fontSize: sf(10),
      fontWeight: '700',
      lineHeight: sf(12),
      textAlign: 'center',
    },
    planCardSelectCol: {
      flexShrink: 0,
      alignSelf: 'stretch',
      alignItems: 'stretch',
      minWidth: 88,
      maxWidth: 112,
    },
    planCardSelectColCompact: {
      flexShrink: 0,
      alignItems: 'flex-end',
      justifyContent: 'center',
    },
    planCardSelectPrice: {
      fontSize: sf(11),
      lineHeight: sf(13),
      fontWeight: '800',
      color: c.onPrimary,
      letterSpacing: -0.15,
      textAlign: 'center',
    },
    planCardActionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: 2,
    },
    planCardMacroRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: appTheme.spacing.sm,
    },
    planCardMacroLine: {
      ...appTheme.typography.caption,
      fontSize: sf(12),
      lineHeight: sf(16),
      fontWeight: '600',
      color: c.textSecondary,
    },
    planCardMetaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: appTheme.spacing.sm,
    },
    planCardMeta: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
    },
    planCardMetaText: {
      ...appTheme.typography.caption,
      fontSize: sf(12),
      fontWeight: '700',
      color: c.text,
    },
    planCardMetaDaysPill: {
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.xs,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      backgroundColor: hexToRgba(c.primary, 0.12),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.primary, 0.24),
    },
    planCardMetaDays: {
      ...appTheme.typography.caption,
      fontSize: sf(12),
      fontWeight: '700',
      color: c.primary,
    },
    planCardViewDetailsWrap: {
      flex: 1,
      paddingVertical: appTheme.spacing.us,
      paddingHorizontal: appTheme.spacing.xs,
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'stretch',
    },
    planCardViewDetailsInner: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    planCardViewDetailsText: {
      ...appTheme.typography.caption,
      fontSize: appTheme.typography.captionMini.fontSize,
      color: c.primary,
      textDecorationLine: 'underline',
    },
    planCardSelectWrap: {
      flex: 1,
      backgroundColor: c.primary,
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: 0,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      alignSelf: 'stretch',
    },
    planCardSelectWrapCompact: {
      flex: 0,
      alignSelf: 'auto',
      paddingVertical: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      minHeight: 0,
    },
    planCardSelectInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
    planCardSelectText: {
      ...appTheme.typography.caption,
      fontSize: sf(11),
      fontWeight: '700',
      color: c.onPrimary,
    },
    welcomeSheetBody: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingBottom: appTheme.spacing.lg,
      alignItems: 'center',
    },
    welcomeIconWrap: {
      width: appTheme.spacing.xxl * 2 + appTheme.spacing.sm,
      height: appTheme.spacing.xxl * 2 + appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.round,
      backgroundColor: (hexToRgba(c.primary, 0.1) as string) || c.primaryUltra,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: appTheme.spacing.md,
    },
    welcomeMessage: {
      ...appTheme.typography.body,
      color: c.textSecondary,
      textAlign: 'center',
      marginBottom: appTheme.spacing.lg,
    },
    welcomeBtn: {
      backgroundColor: c.primary,
      borderRadius: DASH_STUDIO_RADIUS,
      width: '100%',
      overflow: 'hidden',
    },
    welcomeBtnInner: {
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.xxl - appTheme.spacing.xs,
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    welcomeBtnText: {
      ...appTheme.typography.bodyMedium,
      color: c.onPrimary,
      fontWeight: '700',
    },
    freeDaysSheetBody: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingBottom: appTheme.spacing.sm,
    },
    freeDaysMessage: {
      ...appTheme.typography.body,
      color: c.text,
      marginBottom: appTheme.spacing.md,
    },
    freeDaysHighlight: {
      fontWeight: '700',
      color: c.primary,
    },
    freeDaysOptions: {
      gap: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.lg,
    },
    freeDaysOption: {
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: option_row_bg,
      overflow: 'hidden',
    },
    freeDaysOptionInner: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
    },
    freeDaysOptionActive: {
      backgroundColor: option_row_active_bg,
    },
    radioOuter: {
      width: 22,
      height: 22,
      borderRadius: 11,
      backgroundColor: (hexToRgba(c.text, 0.08) as string) || c.borderLight,
      justifyContent: 'center',
      alignItems: 'center',
    },
    radioActive: {
      backgroundColor: (hexToRgba(c.primary, 0.18) as string) || c.primaryFade,
    },
    radioInner: {
      width: 12,
      height: 12,
      borderRadius: 6,
      backgroundColor: c.primary,
    },
    freeDaysOptionText: {
      ...appTheme.typography.body,
      color: c.textSecondary,
    },
    freeDaysOptionTextActive: {
      color: c.text,
      fontWeight: '600',
    },
    freeDaysConfirmBtn: {
      backgroundColor: c.primary,
      borderRadius: DASH_STUDIO_RADIUS,
      overflow: 'hidden',
    },
    freeDaysConfirmInner: {
      paddingVertical: appTheme.spacing.md,
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    freeDaysConfirmText: {
      ...appTheme.typography.bodyMedium,
      color: c.onPrimary,
      fontWeight: '700',
    },
    detailSheetScroll: {
      flexGrow: 1,
    },
    detailSheetScrollContent: {
      paddingBottom: appTheme.spacing.lg,
      flexGrow: 1,
      gap: appTheme.spacing.md,
    },
    detailMetricsHeading: {
      fontSize: sf(12),
      lineHeight: sf(15),
      fontWeight: '800',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: c.textLight,
      marginBottom: appTheme.spacing.sm,
    },
    detailEmptyHint: {
      ...appTheme.typography.body,
      color: c.textSecondary,
      lineHeight: sf(22),
    },
    detailImageWrap: {
      marginHorizontal: appTheme.spacing.md,
      borderRadius: DASH_STUDIO_RADIUS,
      overflow: 'hidden',
      backgroundColor: c.borderLight,
    },
    detailImage: {
      width: '100%',
      aspectRatio: 2,
      backgroundColor: c.borderLight,
    },
    detailBody: {
      paddingHorizontal: appTheme.spacing.md,
      gap: appTheme.spacing.md,
    },
    detailDescription: {
      fontSize: sf(14),
      lineHeight: sf(21),
      fontWeight: '500',
      color: c.textSecondary,
    },
    detailMetricsCard: {
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: c.surfaceElevated ?? c.surface,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.border, 0.45),
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.md,
    },
    detailMetricsChips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 6,
    },
    detailMetricChip: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
      borderWidth: StyleSheet.hairlineWidth * 2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    detailMetricChipText: {
      fontSize: sf(11),
      fontWeight: '700',
      lineHeight: sf(14),
    },
    detailMetricRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
    },
    detailRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.sm,
    },
    detailMetaText: {
      ...appTheme.typography.body,
      color: c.text,
    },
    detailPriceRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: appTheme.spacing.md,
      paddingTop: appTheme.spacing.md,
    },
    detailPriceLabel: {
      ...appTheme.typography.bodyMedium,
      color: c.textSecondary,
    },
    detailPrice: {
      ...appTheme.typography.h3,
      color: c.primary,
    },
    bannerSheetScroll: {
      flexGrow: 0,
    },
    bannerSheetScrollContent: {
      paddingBottom: appTheme.spacing.lg,
    },
    bannerImage: {
      width: '100%',
      aspectRatio: 2,
      backgroundColor: c.borderLight,
    },
    bannerBody: {
      padding: appTheme.spacing.lg,
    },
    bannerDescription: {
      ...appTheme.typography.body,
      color: c.textSecondary,
    },
    categoryPopupSheet: {
      backgroundColor: c.background,
      borderTopLeftRadius: DASH_STUDIO_RADIUS,
      borderTopRightRadius: DASH_STUDIO_RADIUS,
      width: '100%',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    categoryDayBarScroll: {
      flexGrow: 0,
      maxHeight: undefined,
    },
    categoryDayBarScrollContent: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.sm,
      marginHorizontal: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.sm,
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: hexToRgba(c.text, 0.07),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.text, 0.1),
      overflow: 'hidden',
      ...androidFlatSurfaceStyle(),
    },
    categoryDayPill: {
      flexGrow: 1,
      flexShrink: 1,
      minWidth: '22%',
      borderRadius: DASH_STUDIO_RADIUS - 6,
      backgroundColor: 'transparent',
      overflow: 'hidden',
    },
    categoryDayPillActive: {
      backgroundColor: c.primary,
      ...Platform.select({
        ios: {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.14,
          shadowRadius: 4,
        },
        android: { elevation: 2 },
      }),
    },
    categoryDayPillInner: {
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: ss(40),
    },
    categoryDayPillText: {
      ...pillButtonLabelCentered,
      ...appTheme.typography.label,
      fontSize: sf(13),
      color: body_secondary,
      fontWeight: '700',
    },
    categoryDayPillTextActive: {
      color: c.onPrimary,
    },
    categoryPopupPlansScroll: {
      flex: 1,
      backgroundColor: c.background,
    },
    categoryPopupPlansScrollInner: {
      padding: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.xl,
      flexGrow: 1,
      gap: appTheme.spacing.md,
      backgroundColor: c.background,
    },
    categoryPopupContent: {
      flex: 1,
    },
    categoryPopupContentInner: {
      padding: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.lg,
    },
    categoryDayChip: {
      marginBottom: appTheme.spacing.sm,
      overflow: 'visible',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      shadowColor: c.shadowColor,
    },
    categoryDayChipActive: {
      backgroundColor: option_row_active_bg,
    },
    categoryDayChipInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.lg,
      width: '100%',
      overflow: 'hidden',
      borderRadius: DASH_STUDIO_RADIUS,
    },
    categoryDayChipText: {
      ...appTheme.typography.bodyMedium,
      fontSize: sf(16),
      color: c.text,
      fontWeight: '800',
    },
    categoryFlowListIntro: {
      ...studioSectionLabelStyle(c),
      marginBottom: appTheme.spacing.sm,
    },
    categoryFlowListHint: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(14),
      color: c.textSecondary,
      marginBottom: appTheme.spacing.lg,
      lineHeight: sf(20),
    },
    dayGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      rowGap: appTheme.spacing.md,
    },
    listBodyScroll: {
      flex: 1,
    },
    categoryFlowTagGroup: {
      marginBottom: appTheme.spacing.lg,
      gap: appTheme.spacing.sm,
    },
    categoryFlowTagGroupTitle: {
      ...studioSectionLabelStyle(c),
      marginBottom: 0,
    },
    headerBottomChrome: {
      width: '100%',
    },
    customBuilderScroll: {
      flex: 1,
    },
    customBuilderRoot: {
      flex: 1,
    },
    customBuilderContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingBottom: appTheme.spacing.md,
      gap: appTheme.spacing.md,
    },
    customSection: {
      gap: appTheme.spacing.sm,
    },
    customSectionLabel: {
      ...studioSectionLabelStyle(c),
      marginBottom: appTheme.spacing.sm,
    },
    customSectionEyebrow: {
      fontSize: sf(11),
      lineHeight: sf(14),
      fontWeight: '800',
      letterSpacing: 0.45,
      textTransform: 'uppercase',
      color: c.textLight,
    },
    customCategoryRow: {
      gap: 0,
    },
    customCategoryScroll: {
      maxHeight: appTheme.sizes.breadcrumbHeight,
    },
    customComposeCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: c.surface,
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
    },
    customSummaryHeader: {
      gap: 4,
      paddingBottom: appTheme.spacing.xs,
    },
    customSummaryEyebrow: {
      fontSize: sf(11),
      lineHeight: sf(14),
      fontWeight: '800',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: c.textLight,
    },
    customSummaryTitle: {
      fontSize: sf(20),
      lineHeight: sf(26),
      fontWeight: '800',
      letterSpacing: -0.35,
      color: c.text,
    },
    customProgressHint: {
      fontSize: sf(12),
      lineHeight: sf(16),
      fontWeight: '600',
      color: c.textSecondary,
      marginTop: 2,
    },
    // customSetupList: {
    //   borderRadius: PLAN_CARD_CHIP_RADIUS * 2,
    //   backgroundColor: c.primary,
    //   overflow: 'hidden',
    //   gap: 0,
    // },
    customSummaryList: {
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      backgroundColor: c.surfaceElevated,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderSubtle,
      overflow: 'hidden',
    },
    customMealsHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.sm,
      paddingHorizontal: 2,
      marginBottom: 2,
    },
    customMealsHeaderTitleRow: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    customMealsHeaderLabel: {
      flexShrink: 1,
      fontSize: sf(13),
      fontWeight: '700',
      letterSpacing: -0.1,
      color: c.textSecondary,
    },
    customMealsInfoBtn: {
      width: androidScaleSize(28),
      height: androidScaleSize(28),
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 999,
      flexShrink: 0,
    },
    customMealsHeaderCount: {
      fontSize: sf(12),
      fontWeight: '700',
      color: c.primary,
      flexShrink: 0,
    },
    customMacroLegend: {
      fontSize: sf(12),
      fontWeight: '600',
      color: c.textLight,
      paddingHorizontal: 2,
      marginBottom: 4,
      lineHeight: sf(16),
    },
    customMealSlots: {
      gap: appTheme.spacing.sm,
    },
    customMealSlot: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.sm ,
      paddingVertical: androidScaleSize(8),
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      backgroundColor: c.surface,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderSubtle,
      minHeight: androidScaleSize(52),
      ...androidResolveShadow(appTheme.shadows.xs),
    },
    customMealSlotFilled: {
      borderColor: c.successFade,
      backgroundColor: c.surfaceFade,
    },
    customMealIndex: {
      minWidth: androidScaleSize(32),
      height: androidScaleSize(32),
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      backgroundColor: c.surfaceElevated,
      flexGrow: 0,
      flexDirection: 'row',
    },
    customMealIndexIcon: {
      width: androidScaleSize(32),
      height: androidScaleSize(32),
      flexGrow: 1,
    },
    customMealIndexFilled: {
      backgroundColor: c.successUltra,
      flexGrow: 1,
      gap: androidScaleSize(0),
      paddingHorizontal: androidScaleSize(8),
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    customMealIndexText: {
      fontSize: sf(13),
      fontWeight: '800',
      color: c.primary,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    customMealIndexTextFilled: {
        // flex: 1,
        // minWidth: 0,
        fontSize: sf(14),
        fontWeight: '700',
        color: c.success,
        paddingHorizontal: androidScaleSize(8),
        letterSpacing: -0.15,
        ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    customMealSlotBody: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'center',
      gap: 6,
    },
    customMealSlotLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: sf(14),
      fontWeight: '700',
      color: c.text,
      letterSpacing: -0.15,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    customMealSlotTrailing: {
      flexGrow: 2,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 8,
    },
    customMacroChipsRow: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
    },
    customMacroChip: {
      width: androidScaleSize(78),
      height: androidScaleSize(32),
      flexDirection: 'row',
      alignItems: 'center',
      gap: 0,
      paddingLeft: 8,
      paddingRight: 8,
      paddingVertical: 5,
      borderRadius: 10,
      flexShrink: 0,
    },
    customMacroChipKey: {
      width: androidScaleSize(14),
      fontSize: sf(14),
      fontVariant: ['tabular-nums'],
      fontWeight: '800',
      letterSpacing: 0.2,
      textAlign: 'center',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    customMacroChipText: {
      flex: 1,
      minWidth: 0,
      fontSize: sf(13),
      fontWeight: '800',
      letterSpacing: -0.2,
      fontVariant: ['tabular-nums'],
      textAlign: 'right',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    customMacroChipGrams: {
      fontSize: sf(11),
      fontWeight: '700',
    },
    customMacroChipUnit: {
      fontSize: sf(10),
      fontWeight: '700',
    },
    customMealChevron: {
      width: androidScaleSize(18),
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    customMealSelectHint: {
      fontSize: sf(12),
      fontWeight: '600',
      color: c.textLight,
      flexShrink: 1,
    },
    customInfoScroll: {
      maxHeight: 420,
    },
    customInfoBody: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.lg,
      gap: 8,
    },
    customInfoHeading: {
      fontSize: sf(14),
      fontWeight: '800',
      letterSpacing: -0.15,
      marginTop: 6,
    },
    customInfoParagraph: {
      fontSize: sf(14),
      fontWeight: '500',
      lineHeight: sf(20),
    },
    customSummaryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: androidScaleSize(14),
      minHeight: androidScaleSize(50),
    },
    customSummaryRowBorder: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.borderSubtle,
    },
    customMealsSnacksRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      minHeight: androidScaleSize(52),
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: androidScaleSize(8),
      gap: 0,
    },
    customCountPill: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: androidScaleSize(10),
      borderRadius: PLAN_CARD_CHIP_RADIUS - 2,
      backgroundColor: c.background,
    },
    customCountPillDivider: {
      width: StyleSheet.hairlineWidth,
      alignSelf: 'stretch',
      marginVertical: androidScaleSize(6),
      marginHorizontal: 6,
    },
    customCountPillLabel: {
      fontSize: sf(13),
      fontWeight: '600',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    customCountPillValue: {
      fontSize: sf(17),
      fontWeight: '800',
      letterSpacing: -0.3,
      fontVariant: ['tabular-nums'],
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    customSummaryLabel: {
      flexShrink: 0,
      fontSize: sf(14),
      fontWeight: '600',
      lineHeight: sf(18),
    },
    customSummaryValueWrap: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 6,
    },
    customSummaryValue: {
      flexShrink: 1,
      fontSize: sf(15),
      fontWeight: '800',
      lineHeight: sf(18),
      textAlign: 'right',
      letterSpacing: -0.15,
    },
    customStickyFooter: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.md,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderSubtle,
      backgroundColor: c.surface,
      gap: appTheme.spacing.sm,
      ...androidFlatSurfaceStyle(),
    },
    customStickyFooterFade: {
      position: 'absolute',
      top: -appTheme.spacing.md,
      left: 0,
      right: 0,
      height: appTheme.spacing.md,
    },
    customProceedBtn: {
      backgroundColor: c.primary,
      borderRadius: PLAN_CARD_CHIP_RADIUS,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm + 4,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      minHeight: androidScaleSize(56),
      ...androidFlatSurfaceStyle(),
    },
    customProceedInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    },
    customProceedBtnDisabled: {
      opacity: 0.45,
    },
    customProceedPrice: {
      fontSize: sf(16),
      lineHeight: sf(20),
      fontWeight: '800',
      color: c.onPrimary,
      letterSpacing: -0.25,
    },
    customProceedText: {
      fontSize: sf(13),
      fontWeight: '700',
      color: c.onPrimary,
    },
    customEditorBody: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.lg,
      gap: 8,
    },
    customCountGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: 'flex-start',
      width: '100%',
    },
    customCountTile: {
      // Fixed size — `%` width + aspectRatio collapses to a hairline in fitContent sheets
      // (parent width is content-driven, so percentage children resolve to ~0).
      width: count_tile_extent,
      height: count_tile_extent,
      borderRadius: DASH_STUDIO_RADIUS,
      borderWidth: StyleSheet.hairlineWidth,
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
    },
    customCountTileInner: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    customCountTileValue: {
      fontSize: sf(20),
      lineHeight: sf(24),
      fontWeight: '800',
      textAlign: 'center',
      textAlignVertical: 'center',
      fontVariant: ['tabular-nums'],
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    customOptionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: androidScaleSize(14),
      borderRadius: appTheme.borderRadius.lg,
      borderWidth: StyleSheet.hairlineWidth,
      minHeight: androidScaleSize(48),
    },
    customOptionRowCompact: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: androidScaleSize(10),
      borderRadius: appTheme.borderRadius.md,
      borderWidth: StyleSheet.hairlineWidth,
      minHeight: androidScaleSize(40),
    },
    customOptionRowText: {
      flex: 1,
      minWidth: 0,
      fontSize: sf(15),
      fontWeight: '700',
      lineHeight: sf(20),
    },
    customOptionRowTextCompact: {
      fontSize: sf(13),
      fontWeight: '700',
      lineHeight: sf(16),
      textAlign: 'center',
    },
    customMacroColumns: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: appTheme.spacing.sm,
    },
    customMacroColumn: {
      flex: 1,
      minWidth: 0,
      gap: 6,
    },
    customMacroColumnScroll: {
      flexGrow: 0,
    },
    customMacroColumnScrollContent: {
      gap: 6,
      paddingBottom: appTheme.spacing.md,
    },
    customMacroGroupLabel: {
      fontSize: sf(12),
      fontWeight: '800',
      letterSpacing: 0.35,
      textTransform: 'uppercase',
      color: c.textLight,
      marginBottom: 2,
    },
  });
}

export type SubscriptionListStyles = ReturnType<typeof createSubscriptionListStyles>;

